import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';

import { ImageComponent, Image, ResizeHtmlImagesPipe } from '@teamfoster/sdk/image';

import { SwiperContainer } from 'swiper/element/bundle';
import { SwiperOptions } from 'swiper/types';
import { IconComponent } from '@teamfoster/sdk/icon';
import { CookiePreferences, StripEmbedPipe } from '@teamfoster/sdk/cookie-ngrx';
import { SafePipe } from '@teamfoster/sdk/text-utility';

@Component({
  selector: 'app-detail-header-gallery',
  imports: [ImageComponent, IconComponent, IconComponent, SafePipe, ResizeHtmlImagesPipe, StripEmbedPipe, NgClass],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './detail-header-gallery.component.html',
  styleUrl: './detail-header-gallery.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailHeaderGalleryComponent implements AfterViewInit, OnDestroy {
  image = input<Image | null>();
  images = input<Image[] | null>();
  subtitle = input<string>();
  title = input<string>();
  intro = input<string>();
  cookiePreferences = input<CookiePreferences | null>();

  swiperIndex$ = signal(0);
  @ViewChild('swiper') swiperEl?: ElementRef<SwiperContainer>;
  cd = inject(ChangeDetectorRef);

  sliderImages = computed(() => {
    const images = this.images() || [];
    return [...images, ...images, ...images];
  });

  singleImage$ = computed(() => {
    const images = this.images();
    if (images?.length === 1) {
      return images[0];
    }
    if ((this.images()?.length || 0) > 1) {
      return undefined;
    }
    return this.image() || undefined;
  });

  ngAfterViewInit(): void {
    this.initSwiper();
  }

  initSwiper() {
    const that = this;

    const params: SwiperOptions = {
      loop: true,
      autoplay: false,
      spaceBetween: 10,
      slidesPerView: 'auto',
      navigation: false,
      centeredSlides: true,
      mousewheel: {
        forceToAxis: true,
      },
      injectStyles: [
        `
        :host .swiper {
          overflow: visible;
        }
        `,
      ],
      breakpoints: {
        0: {
          spaceBetween: 5,
          //centeredSlides: true,
        },
        768: {
          spaceBetween: 10,
          //centeredSlides: false,
        },
      },
      on: {
        realIndexChange: function (i) {
          that.swiperIndex$.set(i.realIndex);
          that.cd.detectChanges();
        },
        resize: function () {
          that.cd.detectChanges();
        },
      },
    };

    if (this.swiperEl?.nativeElement?.initialize) {
      Object.assign(this.swiperEl.nativeElement, params);
      this.swiperEl.nativeElement.initialize();
      this.cd.detectChanges();
    }
  }

  slideNext() {
    this.swiper?.slideNext();
  }
  slidePrev() {
    this.swiper?.slidePrev();
  }

  get swiper() {
    return this.swiperEl?.nativeElement?.swiper;
  }
  get allowSlideNext() {
    return this.swiper?.allowSlideNext;
  }
  get allowSlidePrev() {
    return this.swiper?.allowSlidePrev;
  }
  get isBeginning() {
    return this.swiper?.isBeginning;
  }
  get isEnd() {
    return (this.swiper?.activeIndex || 0) === (this.images()?.length || 0) - 1;
  }

  ngOnDestroy(): void {
    // CRITICAL: Destroy Swiper instance to prevent memory leaks
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
  }

  preventBack(event: any) {
    if (event?.wheelDeltaX !== 0) {
      event.preventDefault();
    }
  }
}
